import { useDispatch } from "react-redux";
import { generatedV2, useV2GetProjectCountByIdQuery } from "@generated";
import type { V2GetProjectCountByIdApiResponse } from "@generated";
import type { AppDispatch } from "../../store";
import { useActionCable } from "@common/hooks/useActionCable";
import { useApiCable } from "@common/hooks/useApiCable";

// Attribute names of the API's project-counts resource (ProjectCounts::ATTRIBUTES).
export const PROJECT_COUNT_KEYS = [
  "locations",
  "contacts",
  "collaborators",
  "attachments",
  "professional-services",
  "managed-services",
  "quotes",
  "governances",
  "surveys",
  "products",
  "expenses",
  "resources",
  "credits",
  "approvals",
  "versions",
  "notes",
] as const;

export type ProjectCountKey = (typeof PROJECT_COUNT_KEYS)[number];
export type ProjectCounts = Partial<Record<ProjectCountKey, number>>;

type ProjectCountsMessage = {
  data?: { id?: string | number; attributes?: ProjectCounts };
} | null;

// Active-record counts for one project's navigation. Fetched on every mount,
// then kept live by ProjectCountsChannel pushes, which land in the same cache
// entry so every reader updates together.
export function useProjectCounts(
  projectId: string,
  accountSlug: string
): ProjectCounts | undefined {
  const dispatch = useDispatch<AppDispatch>();
  const { data } = useV2GetProjectCountByIdQuery(
    { id: projectId },
    { skip: !projectId, refetchOnMountOrArgChange: true }
  );
  const apiCable = useApiCable(accountSlug);

  useActionCable<ProjectCountsMessage>({
    ...apiCable,
    enabled: apiCable.enabled && Boolean(projectId),
    channel: "ProjectCountsChannel",
    params: { account_slug: accountSlug },
    onReceived: (message) => {
      const id = message?.data?.id;
      if (id === undefined || String(id) !== String(projectId)) return;

      dispatch(
        generatedV2.util.upsertQueryData(
          "v2GetProjectCountById",
          { id: projectId },
          message as V2GetProjectCountByIdApiResponse
        )
      );
    },
  });

  return data?.data?.attributes as ProjectCounts | undefined;
}
